import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { DollarSign, Clock, CheckCircle2, Wallet, TrendingUp } from "lucide-react";

const G    = "#2E9F3A";
const O    = "#F39A1F";
const RED  = "#C9202E";
const INK  = "#0F172A";
const SKY  = "#EAF6FC";

const STATUS_COLOR = {
  pending: { bg: "#FEF3C7", fg: "#92400E", label: "Pending" },
  cleared: { bg: "#DBEAFE", fg: "#1E40AF", label: "Cleared · About To Pay" },
  paid:    { bg: "#D1FAE5", fg: "#065F46", label: "PAID" },
};

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function whenStr(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function MyEarningsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/me/vendor-earnings")
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e.message || "Could not load earnings"));
  }, []);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center">
        <h1 className="font-serif-display text-3xl mb-4" style={{ color: RED }}>Couldn&apos;t Load Your Earnings</h1>
        <p className="text-sm" style={{ color: INK }}>{err}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center text-sm" style={{ color: INK }}>
        Loading your earnings&hellip;
      </div>
    );
  }

  const t = data.totals || {};
  const rows = data.sales || [];

  return (
    <div className="max-w-6xl mx-auto py-6 md:py-8" data-testid="my-earnings-tab">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: O }}>
          ★ MY EARNINGS
        </div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-tight" style={{ color: INK }}>
          What Faitharama Owes You.
        </h1>
        <p className="mt-3 text-base max-w-2xl" style={{ color: INK }}>
          Every time someone you referred buys a Faitharama product, your cut shows up here.
          Pending means we&apos;re waiting on the buyer&apos;s payment to clear. Cleared means
          we&apos;re about to pay you. Paid means it&apos;s already in your pocket.
        </p>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        <StatCard icon={Clock}        color="#92400E" bg="#FEF3C7"
                  label="Pending"     value={money(t.pending_usd)}
                  testid="earnings-pending"
                  sub="Buyer payment still clearing"/>
        <StatCard icon={CheckCircle2} color="#1E40AF" bg="#DBEAFE"
                  label="Cleared"     value={money(t.cleared_usd)}
                  testid="earnings-cleared"
                  sub="About to be paid out"/>
        <StatCard icon={Wallet}       color="#065F46" bg="#D1FAE5"
                  label="Paid Out"    value={money(t.paid_usd)}
                  testid="earnings-paid"
                  sub="Already in your pocket"/>
        <StatCard icon={TrendingUp}   color="#FFFFFF" bg={G}
                  label="Lifetime"    value={money(t.lifetime_usd)}
                  testid="earnings-lifetime"
                  sub="Total you've earned"/>
      </div>

      {/* TABLE */}
      <div className="rounded-xl border-2 overflow-hidden" style={{ borderColor: `${G}33`, background: "#FFFFFF" }}>
        <div className="px-4 py-3 border-b-2 flex items-center justify-between" style={{ borderColor: `${G}33`, background: SKY }}>
          <h2 className="text-sm font-bold tracking-[0.18em] uppercase" style={{ color: INK }}>
            Your Sales Ledger
          </h2>
          <span className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: G }}>
            {rows.length} {rows.length === 1 ? "Sale" : "Sales"}
          </span>
        </div>

        {rows.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: INK }}/>
            <p className="text-base font-bold mb-1" style={{ color: INK }}>No sales yet.</p>
            <p className="text-sm max-w-md mx-auto" style={{ color: INK, opacity: 0.7 }}>
              Share your Mirror Site and your affiliate links. Every time someone you sent
              buys a Faitharama product, the commission you earned shows up right here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="earnings-table">
              <thead style={{ background: "#F8FAFC" }}>
                <tr className="text-left text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: INK }}>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Buyer</th>
                  <th className="px-4 py-3 text-right">Sale</th>
                  <th className="px-4 py-3 text-right">Your Cut</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const st = STATUS_COLOR[r.status] || STATUS_COLOR.pending;
                  return (
                    <tr key={r.id || i} className="border-t" style={{ borderColor: "#E5E7EB" }}
                        data-testid={`earnings-row-${i}`}>
                      <td className="px-4 py-3 whitespace-nowrap" style={{ color: INK }}>{whenStr(r.created_at)}</td>
                      <td className="px-4 py-3 font-medium" style={{ color: INK }}>{r.product_name || r.product_id || "—"}</td>
                      <td className="px-4 py-3" style={{ color: INK, opacity: 0.85 }}>{r.buyer_email || r.buyer_name || "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums" style={{ color: INK }}>{money(r.sale_usd)}</td>
                      <td className="px-4 py-3 text-right font-bold tabular-nums" style={{ color: G }}>{money(r.member_payout_usd)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-bold"
                              style={{ background: st.bg, color: st.fg }}>
                          {st.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* HOW IT WORKS */}
      <div className="mt-8 rounded-xl border-2 p-5 md:p-6" style={{ borderColor: `${O}55`, background: "#FFF8EE" }}>
        <h3 className="text-sm tracking-[0.2em] uppercase font-bold mb-2" style={{ color: O }}>
          ★ How You Get Paid
        </h3>
        <ul className="text-sm space-y-1.5" style={{ color: INK }}>
          <li>• Pending shows up the moment your referral buys.</li>
          <li>• Once the buyer&apos;s payment clears with Stripe (usually 7 days), it moves to Cleared.</li>
          <li>• Mike sends payouts on the 1st and 15th of every month.</li>
          <li>• Once you&apos;re paid, it lands in Paid Out and counts toward your Lifetime total.</li>
        </ul>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, bg, label, value, sub, testid }) {
  const isInverted = bg === G;
  const textColor = isInverted ? "#FFFFFF" : INK;
  return (
    <div className="rounded-xl p-4 shadow-sm border-2" data-testid={testid}
         style={{ background: bg, borderColor: isInverted ? G : "#E5E7EB" }}>
      <div className="flex items-center justify-between mb-1">
        <Icon className="w-5 h-5" style={{ color: isInverted ? "#FFFFFF" : color }}/>
      </div>
      <div className="text-[9px] tracking-[0.22em] uppercase font-bold" style={{ color: isInverted ? "#FFFFFF" : color, opacity: 0.9 }}>{label}</div>
      <div className="text-2xl md:text-3xl font-bold tabular-nums mt-0.5" style={{ color: textColor }}>{value}</div>
      <div className="text-[10px] mt-1 leading-snug" style={{ color: textColor, opacity: 0.75 }}>{sub}</div>
    </div>
  );
}
